import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Checkbox } from 'antd';

import { useFujiAuth } from '../../../auth/useFujiAuth';
import i18n from '../../../assets/languages/configs';
import Header from '../../../components/header/Header';
import GooglePayment from '../../../components/GooglePayment/GooglePayment';
import ApplePayment from '../../../components/ApplePayment/ApplePayment';
import BuyComboItem from '../../../components/BuyComboItem/BuyComboItem';
import FujiModal from '../../../components/fuji-modal/FujiModal';
import TextBreaks from '../../../components/TextBreaks/TextBreaks';
import CenterLoading from '../../../components/CenterLoading/CenterLoading';
import useLocalizedTime from '../../../hooks/useLocalizedTime';
import usePurchaseTicket from '../../../hooks/usePurchaseTicket';
import useLocalizedMoney from '../../../hooks/useLocalizedMoney';
import { calcTotalPrice, convertTextToLinks, getValueByLanguage, isEmptyObject, isOnSaleTicket } from '../../../utils';
import { IS_IN_PURCHASE, PAYMENT_TYPE, PRICE_TYPE, localStorageKeys } from '../../../utils/Constants';
import icNext from '../../../assets/imgs/ic_top.svg?react';
import styles from './styles.module.css';
import CustomAntButton from '../../../components/CustomAntButton/CustomAntButton';
import defaultOptionalForm from '../../../api/data-dummy/default-optional-form.json';
import { useHomeUrls } from '../../../hooks/useHomeUrls';
import { useDispatch } from 'react-redux';
import { useReservationFlow } from '../../../hooks/useReservationFlow';
import { MultilingualType } from 'types';
import { isReactNativeWebView, sendMessageToNativeApp } from '../../../utils/reactNativeUtils';

const ReservationTicketConfirmScreen = () => {
    const [isConfirmChecked, setIsConFirmChecked] = useState(false);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const { t } = useTranslation();
    const { formatTime } = useLocalizedTime();
    const { language } = i18n;
    const l = useCallback((text?: MultilingualType | null) => getValueByLanguage(text, language), [language]);
    const { user, isAuthenticated } = useFujiAuth();
    const {
        isLoading: isTicketInPurchase,
        purchaseWithPayPay,
        hasEmailInUserInfo,
        WarningConfirmModal,
        EmailConfirmModal,
        reservationTicketUpdate,
        purchaseWithNoCost,
    } = usePurchaseTicket();
    const { formatMoney, currencyUnit } = useLocalizedMoney();
    const navigate = useNavigate();
    const { privacy } = useHomeUrls();
    const dispatch = useDispatch();
    const {
        back,
        ticketInfo,
        selectNumber,
        userInfo,
        reservationCombo,
        isEdit,
        anonymousFormAnswers,
        localReservation,
        orderId,
    } = useReservationFlow();

    useEffect(() => {
        if (isEmptyObject(localReservation)) {
            navigate('/buy');
            return;
        }
    }, []);

    const optionalForm = useMemo(() => {
        const visibleOptionalForm = ticketInfo?.optionalForm?.filter((field: any) => field.isVisible);
        const combined: { [key: string]: any } = {};
        defaultOptionalForm?.forEach((item) => {
            combined[item.key] = item;
        });
        visibleOptionalForm?.forEach((item: any) => {
            combined[item.key] = item;
        });
        return Object.values(combined);
    }, [ticketInfo, defaultOptionalForm]);

    const initialListFields = useMemo(() => {
        return userInfo?.listFields ?? [];
    }, [userInfo]);

    const totalPrice = useMemo(() => {
        let priceAdult = 0;
        let priceCustom1 = 0;
        let priceCustom2 = 0;
        let priceCustom3 = 0;

        if (ticketInfo?.priceType === PRICE_TYPE.ADDITION) {
            ticketInfo?.comboItems.forEach((item: any) => {
                if (item.isReserve) {
                    const { reservationInfo } =
                        reservationCombo?.find(
                            (reservation: any) => reservation.displayPriority === item.displayPriority,
                        ) ?? {};
                    priceAdult += reservationInfo?.priceAdult ?? 0;
                    priceCustom1 += reservationInfo?.priceCustom1 ?? 0;
                    priceCustom2 += reservationInfo?.priceCustom2 ?? 0;
                    priceCustom3 += reservationInfo?.priceCustom3 ?? 0;
                } else {
                    priceAdult += item.referencePriceAdult ?? 0;
                    priceCustom1 += item.referencePriceCustom1 ?? 0;
                    priceCustom2 += item.referencePriceCustom2 ?? 0;
                    priceCustom3 += item.referencePriceCustom3 ?? 0;
                }
            });
        } else {
            priceAdult = ticketInfo?.priceAdult ?? 0;
            priceCustom1 = ticketInfo?.priceCustom1 ?? 0;
            priceCustom2 = ticketInfo?.priceCustom2 ?? 0;
            priceCustom3 = ticketInfo?.priceCustom3 ?? 0;
        }

        return {
            priceAdult,
            priceCustom1,
            priceCustom2,
            priceCustom3,
        };
    }, [ticketInfo, reservationCombo]);

    const privacyPolicyUrl = useMemo(() => {
        return l(privacy) ?? privacy?.en;
    }, [language]);

    const shouldShowTotalPrice = useMemo(() => {
        const isCombination = ticketInfo?.isCombination ?? false;
        const isNotAdditionPrice = ticketInfo?.priceType !== PRICE_TYPE.ADDITION;
        return isCombination ? true : isNotAdditionPrice;
    }, [ticketInfo]);

    type ReservationComboRequest = {
        stockId: number;
        ticketId?: number;
        displayPriority?: number;
        originStopId?: number | null;
        destStopId?: number | null;
        reservationId?: string;
        reservationStatus?: string;
    };

    const reservationComboRequest: ReservationComboRequest[] = useMemo(() => {
        return reservationCombo?.map((item: any) => {
            const { stockId, originStop, destStop, reservationId, reservationStatus } = item.reservationInfo ?? {};
            let requestPayload: ReservationComboRequest = {
                stockId: stockId,
                originStopId: originStop?.value ? Number(originStop.value) : null,
                destStopId: destStop?.value ? Number(destStop.value) : null,
            };

            if (isEdit) {
                requestPayload = {
                    ...requestPayload,
                    reservationId: reservationId,
                    reservationStatus: reservationStatus,
                };
            } else {
                requestPayload = { ...requestPayload, ticketId: item.ticketId, displayPriority: item.displayPriority };
            }
            return requestPayload;
        });
    }, [reservationCombo]);

    // 購入ボタン押下時の挙動
    const handlePaymentClick = () => {
        if (!isConfirmChecked) {
            setIsConfirmModalOpen(true);
            return;
        }
        if (!hasEmailInUserInfo()) {
            return;
        }
        navigate('/buy/ticket/credit-payment', {
            state: {
                ticketInfo: {
                    ...ticketInfo,
                    ...totalPrice,
                },
                selectNumber: selectNumber,
                reservationCombo: reservationComboRequest,
                userForm: { ...userInfo.formFields },
                anonymousFormAnswers,
            },
        });
    };

    // 購入ボタン押下時の挙動
    const handlePayPayClick = () => {
        if (!isConfirmChecked) {
            setIsConfirmModalOpen(true);
            return;
        }
        if (!hasEmailInUserInfo()) {
            return;
        }
        void purchaseWithPayPay({
            salesTicketId: ticketInfo?.salesTicketId,
            paymentType: PAYMENT_TYPE.paypay,
            number: selectNumber,
            reservationCombo: reservationComboRequest,
            userForm: { ...userInfo.formFields },
            anonymousFormAnswers,
        });
    };

    // 変更ボタン押下時の挙動
    const handleReservationEdit = async () => {
        if (!isConfirmChecked) {
            setIsConfirmModalOpen(true);
            return;
        }

        const response = await reservationTicketUpdate({
            userForm: { ...userInfo.formFields },
            reservationCombo: reservationComboRequest,
        });
        if (response?.message === 'succeeded') {
            // 予約変更完了
            if (isReactNativeWebView()) {
                // WebView内の場合はネイティブアプリに戻る指示を送る
                sendMessageToNativeApp('go_back', { from: 'reservation_confirm' });
                return;
            }
            navigate('/bought/detail', {
                state: { orderId, orderTicketId: null },
            });
        }
    };

    const handleSelectNumberChange = () => {
        dispatch({
            type: 'RESERVATION_FLOW',
            payload: {
                currentIndex: 0,
            },
        });
        navigate(`/buy/reservation?salesTicketId=${ticketInfo?.salesTicketId}`);
    };

    const handleCalendarChange = () => {
        dispatch({
            type: 'RESERVATION_FLOW',
            payload: {
                currentIndex: 0,
            },
        });
        navigate('/buy/reservation/calendar');
    };

    const handleUserInfoChange = () => {
        dispatch({
            type: 'RESERVATION_FLOW',
            payload: {
                currentIndex: 0,
            },
        });
        navigate(`/buy/reservation/user-info`);
    };

    const handleLoginClick = () => {
        localStorage.setItem(IS_IN_PURCHASE, 'true');
        localStorage.setItem(
            localStorageKeys.LOGIN_FLOW,
            JSON.stringify({
                path: `/buy/reservation/confirm`,
            }),
        );
        navigate('/auth');
    };

    const canUpdateReservation = (reservationCombo: any) => {
        return reservationCombo.some(({ reservationInfo }: any) => {
            const { initValue } = reservationInfo;
            const { oldUserInfo, oldReservationInfo } = initValue;

            const hasStockChange =
                reservationInfo.stockId !== oldReservationInfo.stockId ||
                String(reservationInfo?.originStop?.value) !== String(oldReservationInfo.originStopId) ||
                String(reservationInfo?.destStop?.value) !== String(oldReservationInfo.destStopId);

            const oldFormFields = oldUserInfo.formFields ?? {};
            const newFormFields = userInfo.formFields ?? {};
            if (Object.keys(newFormFields).length === 0) {
                return true;
            }
            const hasFormChange = Object.entries(newFormFields).some(([key, value]) => {
                if (key === 'confirm') {
                    return false;
                }
                return oldFormFields?.[key] == null && value == null
                    ? false
                    : JSON.stringify(oldFormFields?.[key]) !== JSON.stringify(value);
            });

            return hasStockChange || hasFormChange;
        });
    };

    const formatReservationFieldValue = (key: string, value: any) => {
        if (value == null || value === '') return null;

        if (key === 'confirm') {
            return null;
        }
        if (key === 'phoneNumber') {
            const dialCode = userInfo.formFields?.countryCode?.dialCode;
            return `${dialCode ? dialCode + '-' : ''}${value}`;
        }
        if (value && String(value).includes('option=')) {
            const field = optionalForm.find((field) => field.key === key && field.type === 'enum');
            if (!field) {
                return null;
            }
            return l(
                field.options?.find((option: any) => String(option.optionKey) === String(value).replace('option=', '')),
            );
        }

        return value;
    };

    const handlePurchaseNoCostTicket = async () => {
        if (!isConfirmChecked) {
            setIsConfirmModalOpen(true);
            return;
        }
        void purchaseWithNoCost({
            salesTicketId: ticketInfo.salesTicketId,
            number: selectNumber,
            anonymousFormAnswers,
            reservationCombo: reservationComboRequest,
            userForm: { ...userInfo.formFields },
        });
    };

    return (
        <>
            <FujiModal
                isOpen={isConfirmModalOpen}
                onSubmit={() => {
                    setIsConfirmModalOpen(false);
                }}
                txtSubmit={'OK'}
                body={t('reservation.note-confirm')}
            />
            <WarningConfirmModal />
            <EmailConfirmModal />
            {isTicketInPurchase && <CenterLoading />}
            <Header title={!isEdit ? t('header.buy-ticket') : t('reservation.header')} callback={back} />
            {!isEmptyObject(localReservation) && (
                <div className="content-container scrollable">
                    {/* <p className={styles.screenName}>{t('reservation.ticket-info')}</p> */}
                    <div className={styles.importantPoint}>
                        {isEdit
                            ? t('reservation.not-confirmed-edit', {
                                  'btn-text': t('button.confirm-change-reservation'),
                              })
                            : ticketInfo.priceType === PRICE_TYPE.NO_COST
                              ? t('reservation.not-confirmed-no-cost')
                              : t('reservation.not-confirmed')}
                    </div>
                    {/* チケットの情報 */}
                    <div className={`${styles.ticketInfo} `}>
                        <div className={`${styles.topContent} ${ticketInfo?.isCombination ? '' : styles.borderBottom}`}>
                            <h4 className={styles.ticketName}>
                                <TextBreaks text={l(ticketInfo?.ticketName)} />
                            </h4>
                            <p className={styles.descriptions}>
                                <TextBreaks text={l(ticketInfo?.subtitle)} />
                            </p>
                        </div>
                    </div>
                    {ticketInfo?.isCombination && (
                        <BuyComboItem
                            ticketInfo={ticketInfo}
                            comboItem={ticketInfo?.comboItems}
                            reservationCombo={reservationCombo}
                            isConfirm={true}
                            selectNumber={selectNumber}
                        />
                    )}
                    <div className={`${styles.userInfo} ${ticketInfo?.isCombination ? styles.marginTop : ''}`}>
                        {/*人数*/}
                        <div className={styles.textContent}>
                            {/* <div className={styles.value}>{t('reservation.number-of-people')}</div> */}
                            <div className={styles.value}>
                                {(selectNumber.adult ?? 0) > 0 && (
                                    <span className={styles.numberContext}>
                                        {t('ticket-detail.number', {
                                            label: t('ticket-detail.adult-label'),
                                            number: selectNumber.adult,
                                        })}
                                    </span>
                                )}
                                {(selectNumber.custom1 ?? 0) > 0 && (
                                    <span className={styles.numberContext}>
                                        {t('ticket-detail.number', {
                                            label: getValueByLanguage(ticketInfo?.custom1Name, language),
                                            number: selectNumber.custom1,
                                        })}
                                    </span>
                                )}
                                {(selectNumber.custom2 ?? 0) > 0 && (
                                    <span className={styles.numberContext}>
                                        {t('ticket-detail.number', {
                                            label: getValueByLanguage(ticketInfo?.custom2Name, language),
                                            number: selectNumber.custom2,
                                        })}
                                    </span>
                                )}
                                {(selectNumber.custom3 ?? 0) > 0 && (
                                    <span className={styles.numberContext}>
                                        {t('ticket-detail.number', {
                                            label: getValueByLanguage(ticketInfo?.custom3Name, language),
                                            number: selectNumber.custom3,
                                        })}
                                    </span>
                                )}
                            </div>
                            {!isEdit ? (
                                <div className={styles.changeButton} onClick={handleSelectNumberChange}>
                                    <p className={styles.buttonText}>{t('reservation.change-number-of-people')}</p>
                                    <div className={styles.layout}>
                                        {React.createElement(icNext, {
                                            className: `${styles.icon}`,
                                        })}
                                    </div>
                                </div>
                            ) : (
                                <div className={`${styles.changeButton} ${styles.buttonText}`}>
                                    {t('reservation.cannot-change-number-of-people')}
                                </div>
                            )}
                        </div>
                        <div className={styles.textContent}>
                            {reservationCombo.map((item: any) => (
                                <div key={item.displayPriority} className={styles.reservationItem}>
                                    {/* <div className={styles.value}>{t('reservation.selected-ticket')}</div> */}
                                    <p className={styles.descriptions}>
                                        {t('reservation.usage-time')}:{' '}
                                        {formatTime(item.reservationInfo?.reserveOn, 'lll')}
                                    </p>
                                    <p className={styles.descriptions}>
                                        <TextBreaks text={l(item.reservationInfo?.stockPartitionName)} />
                                    </p>
                                    <p className={styles.descriptions}>
                                        <TextBreaks text={l(item.reservationInfo?.reservationClassName)} />
                                        {!l(item.reservationInfo?.reservationClassName) ||
                                        !l(item.reservationInfo?.reservationPriceDescription)
                                            ? ''
                                            : '/ '}

                                        <TextBreaks text={l(item.reservationInfo?.reservationPriceDescription)} />
                                    </p>
                                    {item.reservationInfo?.originStop?.shortName &&
                                        item.reservationInfo?.destStop?.shortName && (
                                            <p className={styles.descriptions}>
                                                <TextBreaks text={l(item.reservationInfo?.originStop?.shortName)} />
                                                <span style={{ margin: '0 5px' }}>→</span>
                                                <TextBreaks text={l(item.reservationInfo?.destStop?.shortName)} />
                                            </p>
                                        )}
                                    <p className={styles.descriptions}>
                                        <TextBreaks text={l(item.reservationInfo?.stockPartitionDescription)} />
                                    </p>
                                    {ticketInfo?.priceType === PRICE_TYPE.ADDITION && (
                                        <h4 className={styles.ticketName}>
                                            {formatMoney(
                                                calcTotalPrice(selectNumber, {
                                                    adult: item.reservationInfo?.priceAdult,
                                                    custom1: item.reservationInfo?.priceCustom1 ?? 0,
                                                    custom2: item.reservationInfo?.priceCustom2 ?? 0,
                                                    custom3: item.reservationInfo?.priceCustom3 ?? 0,
                                                }),
                                            ) + currencyUnit('JPY')}
                                        </h4>
                                    )}
                                </div>
                            ))}
                            <div className={styles.changeButton} onClick={handleCalendarChange}>
                                <p className={styles.buttonText}>{t('reservation.change-ticket')}</p>
                                <div className={styles.layout}>
                                    {React.createElement(icNext, {
                                        className: `${styles.icon}`,
                                    })}
                                </div>
                            </div>
                        </div>

                        <div className={styles.textContent}>
                            {/* <div className={styles.value}>{t('reservation.user-info')}</div> */}
                            {initialListFields.map((field: any) => {
                                const { key, value, label } = field ?? {};

                                const formatValue = formatReservationFieldValue(key, value);
                                if (!formatValue && formatValue !== 0) {
                                    return '';
                                }
                                return (
                                    <p key={key} className={styles.descriptions}>
                                        {l(label) + ': ' + formatValue}
                                    </p>
                                );
                            })}
                            <div className={styles.changeButton} onClick={handleUserInfoChange}>
                                <p className={styles.buttonText}>{t('reservation.change-user-info')}</p>
                                <div className={styles.layout}>
                                    {React.createElement(icNext, {
                                        className: `${styles.icon}`,
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className={styles.priceWrapper}>
                        {/* 合計金額 */}
                        {shouldShowTotalPrice && (
                            <div className={styles.totalPrice}>
                                {t('ticket-detail.total-amount')}
                                <span className={styles.price}>
                                    {formatMoney(
                                        calcTotalPrice(selectNumber, {
                                            adult: totalPrice?.priceAdult ?? 0,
                                            custom1: totalPrice?.priceCustom1 ?? 0,
                                            custom2: totalPrice?.priceCustom2 ?? 0,
                                            custom3: totalPrice?.priceCustom3 ?? 0,
                                        }),
                                    )}
                                </span>
                                {currencyUnit('JPY')}
                                <span className={styles.taxIncluded}>{t('ticket-detail.tax-included')}</span>
                            </div>
                        )}
                        {ticketInfo?.reservationPriceDescription && (
                            <p className={styles.textNote}>
                                {convertTextToLinks(l(ticketInfo?.reservationPriceDescription))}
                            </p>
                        )}
                        {ticketInfo?.note && (
                            <p className={styles.textNote}>{convertTextToLinks(l(ticketInfo?.note))}</p>
                        )}
                    </div>
                    {/* 確認チェックボックス */}

                    {userInfo != null && (
                        <div className={styles.checkboxWrapper}>
                            <a href={privacyPolicyUrl} target="_blank" rel="noopener noreferrer">
                                {t('reservation.note-info')}
                            </a>
                            <div className={styles.confirmBox}>
                                <Checkbox
                                    onChange={() => setIsConFirmChecked((preState) => !preState)}
                                    className={styles.checkbox}
                                >
                                    {t('reservation.note')}
                                </Checkbox>
                            </div>
                        </div>
                    )}

                    <div className={styles.btnArea}>
                        {isOnSaleTicket(ticketInfo) ? (
                            <>
                                {isAuthenticated ? (
                                    !isEdit ? (
                                        <>
                                            {ticketInfo.priceType === PRICE_TYPE.NO_COST ? (
                                                <CustomAntButton
                                                    className={styles.coloredBtn}
                                                    block
                                                    onClick={handlePurchaseNoCostTicket}
                                                >
                                                    {t('button.reservation-no-cost-ticket')}
                                                </CustomAntButton>
                                            ) : (
                                                <>
                                                    <p>{t('ticket-detail.paymentType.before')}</p>
                                                    <CustomAntButton
                                                        type="payment"
                                                        className={styles.paymentBtn}
                                                        block
                                                        onClick={handlePaymentClick}
                                                    >
                                                        {t('ticket-detail.paymentType.credit-card')}
                                                    </CustomAntButton>
                                                    <GooglePayment
                                                        ticketInfo={{
                                                            ...ticketInfo,
                                                            ...totalPrice,
                                                        }}
                                                        selectNumber={selectNumber}
                                                        isConfirmChecked={isConfirmChecked}
                                                        reservationCombo={reservationComboRequest}
                                                        userForm={{ ...userInfo.formFields }}
                                                        onClick={() => setIsConfirmModalOpen(true)}
                                                        anonymousFormAnswers={anonymousFormAnswers}
                                                    />
                                                    <ApplePayment
                                                        ticketInfo={{
                                                            ...ticketInfo,
                                                            ...totalPrice,
                                                        }}
                                                        selectNumber={selectNumber}
                                                        isConfirmChecked={isConfirmChecked}
                                                        reservationCombo={reservationComboRequest}
                                                        userForm={{ ...userInfo.formFields }}
                                                        email={user.email}
                                                        anonymousFormAnswers={anonymousFormAnswers}
                                                        onClick={() => setIsConfirmModalOpen(true)}
                                                    />
                                                    <CustomAntButton
                                                        type="payment"
                                                        className={styles.paymentBtn}
                                                        block
                                                        onClick={handlePayPayClick}
                                                    >
                                                        {t('ticket-detail.paymentType.paypay')}
                                                    </CustomAntButton>
                                                </>
                                            )}
                                        </>
                                    ) : (
                                        <CustomAntButton
                                            className={styles.coloredBtn}
                                            block
                                            onClick={handleReservationEdit}
                                            {...(canUpdateReservation(reservationCombo)
                                                ? { disabled: false, type: 'primary' }
                                                : { disabled: true, type: 'disabled' })}
                                        >
                                            {t('button.confirm-change-reservation')}
                                        </CustomAntButton>
                                    )
                                ) : (
                                    <CustomAntButton className={styles.coloredBtn} block onClick={handleLoginClick}>
                                        {t('ticket-detail.login-and-purchase')}
                                    </CustomAntButton>
                                )}
                            </>
                        ) : (
                            <CustomAntButton type="disabled" className={styles.disableBtn} block disabled>
                                {t('button.not-on-sale')}
                            </CustomAntButton>
                        )}
                    </div>
                </div>
            )}
        </>
    );
};
export default ReservationTicketConfirmScreen;
